import { Box, Flex, Section } from "@radix-ui/themes";
import { Navbar } from "../components/Navbar";
import { Countdown } from "../components/Countdown.tsx";
import { useEffect, useRef, useState } from "react";
import ArticleContainer from "../components/ArticleContainer.tsx";

interface PlayerScoreBoard {
  playername: string;
  score: number;
  position: number;
}

const POLL_INTERVAL_MS = 10000;

// ro-dispatcher regenerates leaderboard.json on its own every ~15s now (no
// more manually running build_leaderboard.py) — this page polls to match,
// so a screen left open (e.g. at the table) keeps updating on its own.
function leaderboardUrl(): string {
  return window.location.href.includes("localhost")
    ? "http://localhost:1244/leaderboard.json"
    : window.location.href.includes("dev")
      ? "https://dev.reali7y-over.run/leaderboard.json"
      : "https://reali7y-over.run/leaderboard.json";
}

const MEDAL: Record<number, { color: string; glow: string; label: string }> = {
  1: { color: "#e8c468", glow: "rgba(232,196,104,0.55)", label: "1st" },
  2: { color: "var(--ro-silver-bright)", glow: "rgba(200,205,210,0.5)", label: "2nd" },
  3: { color: "#c17a4a", glow: "rgba(193,122,74,0.5)", label: "3rd" },
};

function Leaderboard() {
  const [leaderboard, setLeaderboard] = useState<PlayerScoreBoard[]>([]);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const inFlight = useRef(false);

  useEffect(() => {
    let cancelled = false;

    const poll = () => {
      if (inFlight.current) return; // skip if the previous fetch is still out
      inFlight.current = true;
      fetch(leaderboardUrl(), {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      })
        .then((r) => r.json())
        .then((data: PlayerScoreBoard[]) => {
          if (cancelled) return;
          // Keep the last-good board on a malformed response rather than
          // flashing empty — a screen left running for hours shouldn't
          // blank out over one bad poll.
          if (Array.isArray(data)) {
            setLeaderboard(data);
            setError(false);
          }
          setLastUpdated(new Date());
          setLoading(false);
        })
        .catch(() => {
          if (cancelled) return;
          setError(true);
          setLoading(false); // don't hang on "Loading..." forever if the very first fetch fails
        })
        .finally(() => {
          inFlight.current = false;
        });
    };

    poll();
    const id = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  const secondsAgo = lastUpdated
    ? Math.max(0, Math.round((Date.now() - lastUpdated.getTime()) / 1000))
    : null;

  return (
    <Box>
      <Navbar />
      <Countdown />
      <Flex justify={"center"}>
        <Box width={"700px"} style={{ maxWidth: "94vw" }}>
          <Section>
            <ArticleContainer>
              <Flex justify="between" align="center" style={{ marginBottom: 18 }}>
                <div
                  style={{
                    fontFamily: "var(--ro-font-display)",
                    fontSize: "1.6em",
                    letterSpacing: "0.08em",
                    color: "var(--ro-accent-bright)",
                    textShadow: "0 0 14px var(--ro-accent-glow)",
                  }}
                >
                  LEADERBOARD
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    fontSize: ".8em",
                    color: error ? "#e07a7a" : "var(--ro-text-dim)",
                  }}
                >
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: error ? "#e07a7a" : "var(--ro-accent)",
                      boxShadow: error
                        ? "none"
                        : "0 0 6px var(--ro-accent-glow)",
                      display: "inline-block",
                      animation: error ? "none" : "ro-lb-pulse 2s infinite",
                    }}
                  />
                  {error
                    ? "connection lost — showing last known standings"
                    : secondsAgo !== null
                      ? `updated ${secondsAgo}s ago`
                      : "loading..."}
                </div>
              </Flex>

              <style>{`
                @keyframes ro-lb-pulse {
                  0%, 100% { opacity: 1; }
                  50% { opacity: 0.35; }
                }
              `}</style>

              {loading ? (
                <Box style={{ color: "var(--ro-text-dim)", padding: "20px 0" }}>
                  Loading standings...
                </Box>
              ) : leaderboard.length === 0 ? (
                <Box style={{ color: "var(--ro-text-dim)", padding: "20px 0" }}>
                  No scores yet. Go earn one.
                </Box>
              ) : (
                <Box>
                  {leaderboard.map((player) => {
                    const medal = MEDAL[player.position];
                    return (
                      <Flex
                        key={player.playername}
                        align="center"
                        style={{
                          padding: "10px 12px",
                          marginBottom: 6,
                          borderRadius: 5,
                          border: `1px solid ${
                            medal ? medal.color : "var(--ro-border)"
                          }`,
                          background: medal
                            ? `linear-gradient(90deg, ${medal.glow} 0%, transparent 60%)`
                            : "var(--ro-panel)",
                        }}
                      >
                        <Box
                          style={{
                            width: 42,
                            fontFamily: "var(--ro-font-display)",
                            fontWeight: "bold",
                            fontSize: medal ? "1.05em" : "0.95em",
                            color: medal ? medal.color : "var(--ro-text-dim)",
                            textShadow: medal ? `0 0 10px ${medal.glow}` : "none",
                          }}
                        >
                          {medal ? medal.label : `#${player.position}`}
                        </Box>
                        <Box style={{ flex: 1, color: "var(--ro-text)" }}>
                          {player.playername}
                        </Box>
                        <Box
                          style={{
                            fontWeight: "bold",
                            color: medal ? medal.color : "var(--ro-accent-bright)",
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          {player.score.toLocaleString()}
                        </Box>
                      </Flex>
                    );
                  })}
                </Box>
              )}
            </ArticleContainer>
          </Section>
        </Box>
      </Flex>
    </Box>
  );
}

export default Leaderboard;
